import { Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ConfirmationService, TreeNode } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialog } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { DividerModule } from "primeng/divider";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FluidModule } from "primeng/fluid";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectModule } from "primeng/select";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { TreeModule } from "primeng/tree";
import { Observable, of, switchMap } from "rxjs";
import { EditAccessRoleDialogConfig } from "./edit-access-role-dialog.types";
import { ApiUpsertAccessRoleRequest } from "../../../../apiClient/models";
import {
  AccessRoleColor,
  AccessRoleColorKind,
  AccessRoleColorSeverityMapping,
  AccessRoleDetails,
} from "../../../../domain/access-role";
import { EnabledProcessDetails } from "../../../../domain/enabled-process-details";
import { ExtraServiceStateKind, RanExpenseAccess, RanExpenseAccessKind } from "../../../../domain/extra-service";
import { OrganizationDetails } from "../../../../domain/organization";
import { AccessRoleService } from "../../../../services/access-role.service";
import { FeatureFlagService } from "../../../../services/logical/feature-flag.service";
import { OrganizationService } from "../../../../services/organization.service";
import { ToastService } from "../../../../services/toast.service";
import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";
import { PrimeNgSeverity } from "../../../../shared/types/primeng";

interface ServiceFeatureData {
  type: "service" | "feature";
  id: number;
  isValidSelection: boolean; // Determines whether or not this node can EVER be selected
  isVisibleToEmployeeUsers: boolean; // Access is implicitly granted when `canAccessEmployeeProcesses` is true
}

@Component({
  templateUrl: "./edit-access-role.dialog.html",
  imports: [
    ...primeNgPageModules,
    DialogModule,
    ButtonModule,
    FluidModule,
    TranslocoDirective,
    ConfirmDialog,
    InputTextModule,
    ReactiveFormsModule,
    DividerModule,
    CheckboxModule,
    TreeModule,
    SelectModule,
    RadioButtonModule,
    TagModule,
    TooltipModule,
  ],
  providers: [ConfirmationService],
})
export class EditAccessRoleDialog {
  public accessRole: AccessRoleDetails | null;
  public isCreateMode: boolean;
  private organization: OrganizationDetails;
  public loading = false;
  public treeLoading = true;
  // TODO: Remove this once the extraServices feature flag is removed. Hides the extra-service permission checkboxes when off.
  public readonly extraServicesEnabled: boolean;
  public readonly showRanExpensePermissions: boolean;
  public accessRoleColorOptions: { label: string; value: AccessRoleColor; severity: PrimeNgSeverity }[] = [];

  /**
   * t(app.organization-settings.editAccessRoleDialog.form.ranExpense.admin)
   * t(app.organization-settings.editAccessRoleDialog.form.ranExpense.adminTooltip)
   * t(app.organization-settings.editAccessRoleDialog.form.ranExpense.regular)
   * t(app.organization-settings.editAccessRoleDialog.form.ranExpense.regularTooltip)
   * t(app.organization-settings.editAccessRoleDialog.form.ranExpense.none)
   */
  public readonly ranExpenseAccessOptions: { value: RanExpenseAccess; labelKey: string; tooltip?: string }[] = [
    {
      value: RanExpenseAccessKind.Admin,
      labelKey: "app.organization-settings.editAccessRoleDialog.form.ranExpense.admin",
      tooltip: "app.organization-settings.editAccessRoleDialog.form.ranExpense.adminTooltip",
    },
    {
      value: RanExpenseAccessKind.Regular,
      labelKey: "app.organization-settings.editAccessRoleDialog.form.ranExpense.regular",
      tooltip: "app.organization-settings.editAccessRoleDialog.form.ranExpense.regularTooltip",
    },
    {
      value: RanExpenseAccessKind.None,
      labelKey: "app.organization-settings.editAccessRoleDialog.form.ranExpense.none",
    },
  ];

  /* Reactive Form */
  public form = new FormGroup({
    name: new FormControl<string>("", [Validators.required, Validators.minLength(1)]),
    color: new FormControl<AccessRoleColor>(AccessRoleColorKind.Blue, { nonNullable: true }),
    canAcceptTos: new FormControl<boolean>(false, { nonNullable: true }),
    canAccessEmployeeProcesses: new FormControl<boolean>(false, { nonNullable: true }),
    canModifyAccessRoles: new FormControl<boolean>(false, { nonNullable: true }),
    canModifyApprovalGroups: new FormControl<boolean>(false, { nonNullable: true }),
    canModifyInvitations: new FormControl<boolean>(false, { nonNullable: true }),
    canModifyUsers: new FormControl<boolean>(false, { nonNullable: true }),
    canRemoveUsers: new FormControl<boolean>(false, { nonNullable: true }),
    canSeeResponsiblePersons: new FormControl<boolean>(false, { nonNullable: true }),
    canShareProcesses: new FormControl<boolean>(false, { nonNullable: true }),
    canApproveProcesses: new FormControl<boolean>(false, { nonNullable: true }),
    canModifyAutomationUsers: new FormControl<boolean>(false, { nonNullable: true }),
    canViewAllServices: new FormControl<boolean>(false, { nonNullable: true }),
    canViewAllPrivateTransactions: new FormControl<boolean>(false, { nonNullable: true }),
    canViewAuditLog: new FormControl<boolean>(false, { nonNullable: true }),
    canModifyExtraServices: new FormControl<boolean>(false, { nonNullable: true }),
    canViewExtraServices: new FormControl<boolean>(false, { nonNullable: true }),
    ranExpenseAccess: new FormControl<RanExpenseAccess>(RanExpenseAccessKind.None, { nonNullable: true }),
    accessToProcessParents: new FormControl<number[]>([], { nonNullable: true }),
  });

  /* Service/Feature Tree */
  public serviceTree: TreeNode<ServiceFeatureData>[] = [];
  public selectedServiceNodes: TreeNode<ServiceFeatureData>[] = [];

  // Used for restoring user's selection when canAccessEmployeeProcesses is toggled off
  // and the employee-visible nodes are no longer implicitly granted.
  private _explicitlySelectedEmployeeIds = new Set<number>();

  constructor(
    public readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig<EditAccessRoleDialogConfig>,
    private readonly translocoService: TranslocoService,
    private readonly organizationService: OrganizationService,
    private readonly accessRoleService: AccessRoleService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly toastService: ToastService,
    private readonly confirmationService: ConfirmationService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.accessRole = this.config.data!.accessRole;
    this.isCreateMode = this.accessRole === null;
    this.organization = this.config.data!.organization;
    // TODO(extra-services): Delete this flag after full release
    this.extraServicesEnabled = this.featureFlagService.isEnabled("extraServices");
    this.showRanExpensePermissions = this.organization.extraServices.some(
      (service) => service.isRanExpense && service.state !== ExtraServiceStateKind.Inactive,
    );
    if (this.accessRole) {
      // Use `.has()` for implied-by-canViewAllServices permissions so built-in roles with
      // canViewAllServices=true render those checkboxes as checked.
      this.form.patchValue({
        name: this.accessRole.name,
        color: this.accessRole.color,
        canAcceptTos: this.accessRole.canAcceptTos,
        canAccessEmployeeProcesses: this.accessRole.has("canAccessEmployeeProcesses"),
        canModifyAccessRoles: this.accessRole.canModifyAccessRoles,
        canModifyApprovalGroups: this.accessRole.canModifyApprovalGroups,
        canModifyInvitations: this.accessRole.canModifyInvitations,
        canModifyUsers: this.accessRole.canModifyUsers,
        canRemoveUsers: this.accessRole.canRemoveUsers,
        canModifyAutomationUsers: this.accessRole.canModifyAutomationUsers,
        canSeeResponsiblePersons: this.accessRole.has("canSeeResponsiblePersons"),
        canShareProcesses: this.accessRole.has("canShareProcesses"),
        canApproveProcesses: this.accessRole.canApproveProcesses,
        canViewAllServices: this.accessRole.canViewAllServices,
        canViewAllPrivateTransactions: this.accessRole.canViewAllPrivateTransactions,
        canViewAuditLog: this.accessRole.canViewAuditLog,
        canModifyExtraServices: this.accessRole.canModifyExtraServices,
        canViewExtraServices: this.accessRole.canViewExtraServices,
        ranExpenseAccess: this.accessRole.ranExpenseAccess,
        accessToProcessParents: this.accessRole.accessToProcessParents.map((p) => p.processParentId),
      });

      // If the access role is a built-in role, disable the form except the RAN Expense radio, which stays
      // editable (it's the one thing we allow changing for built-in roles, via the dedicated endpoint).
      if (this.accessRole.isBuiltInRole) {
        this.form.disable();
        if (this.showRanExpensePermissions) {
          this.form.controls.ranExpenseAccess.enable();
        }
      }
    }

    // Set up reactive form logic for both create and edit (skip for built-in roles)
    if (!this.accessRole?.isBuiltInRole) {
      this._setupFormLogic();
    }

    /**
     * t(app.accessRoles.colorOptions.blue)
     * t(app.accessRoles.colorOptions.green)
     * t(app.accessRoles.colorOptions.red)
     * t(app.accessRoles.colorOptions.yellow)
     */
    this.accessRoleColorOptions = Object.values(AccessRoleColorKind).map((color) => ({
      label: this.translocoService.translate(`app.accessRoles.colorOptions.${color.toLowerCase()}`),
      value: color,
      severity: AccessRoleColorSeverityMapping[color] ?? "info",
    }));

    // Load real service/feature tree from API
    this._loadServiceTree();
  }

  private _loadServiceTree(): void {
    const selectedIds = new Set(this.accessRole?.accessToProcessParents.map((p) => p.processParentId) ?? []);

    this.organizationService
      .getOrganizationEnabledProcessParents$(this.organization.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (processParents) => {
          const { tree, selected } = this._buildServiceTree(processParents, selectedIds);
          this.serviceTree = tree;
          this.selectedServiceNodes = selected;
          this._syncTreeSelectionToForm({ markDirty: false });
          this._updateTreeDisabledStates();
          this.treeLoading = false;

          this._explicitlySelectedEmployeeIds = this._collectExplicitlySelectedEmployeeIds();

          if (this.form.controls.canViewAllServices.value) {
            this._selectAllServiceNodes();
            this._disableServiceTree(true);
          } else {
            if (this.form.controls.canAccessEmployeeProcesses.value) {
              // Apply employee-visible selections now that the tree nodes actually exist.
              // This also runs for built-in roles (e.g. employee) so their implicit grants show as checked.
              this._applyCanAccessEmployeeProcessesRules(true, { markDirty: false });
            }
            if (this.accessRole?.isBuiltInRole) {
              this._disableServiceTree(true);
            }
          }
        },
        error: () => {
          this.toastService.error(this.translocoService.translate("app.common.errors.loadProcesses"));
          this.treeLoading = false;
        },
      });
  }

  private _buildServiceTree(
    enabledProcessDetails: EnabledProcessDetails,
    selectedIds: Set<number>,
  ): { tree: TreeNode<ServiceFeatureData>[]; selected: TreeNode<ServiceFeatureData>[] } {
    const tree: TreeNode<ServiceFeatureData>[] = [];
    const selected: TreeNode<ServiceFeatureData>[] = [];

    for (const item of enabledProcessDetails.serviceHierarchy.services) {
      if (item.isHiddenFromPermissions) {
        continue;
      }

      const isServiceEnabled = enabledProcessDetails.enabledProcessParentIds.some((x) => x === item.processParentId);
      const featureNodes: TreeNode<ServiceFeatureData>[] = [];

      for (const feature of item.features) {
        // A Feature is enabled when it's directly in enabledProcessParentIds, or when its parent Service is enabled,
        // or when one of the processes under it is enabled.
        const isFeatureEnabled =
          enabledProcessDetails.enabledProcessParentIds.some((x) => x === feature.processParentId) ||
          enabledProcessDetails.enabledProcessInfo.some((p) => p.parentId === feature.processParentId) ||
          isServiceEnabled;
        if (!isFeatureEnabled) {
          continue;
        }
        const featureNode: TreeNode<ServiceFeatureData> = {
          key: `pp-${feature.processParentId}`,
          label: feature.translations["name"] ?? "",
          data: {
            type: "feature",
            id: feature.processParentId,
            isValidSelection: true,
            isVisibleToEmployeeUsers: feature.isVisibleToEmployeeUsers,
          },
          selectable: true,
          leaf: true,
        };
        featureNodes.push(featureNode);

        if (selectedIds.has(feature.processParentId)) {
          selected.push(featureNode);
        }
      }

      const serviceNode: TreeNode<ServiceFeatureData> = {
        key: `pp-${item.processParentId}`,
        label: item.translations["name"] ?? "",
        icon: item.iconString ? `pi ${item.iconString}` : undefined,
        data: {
          type: "service",
          id: item.processParentId,
          isValidSelection: isServiceEnabled,
          isVisibleToEmployeeUsers: item.isVisibleToEmployeeUsers,
        },
        expanded: true,
        // ...but Services which aren't enabled for the org still need to be visible, but not selectable.
        selectable: isServiceEnabled,
        children: featureNodes,
      };
      tree.push(serviceNode);

      if (selectedIds.has(item.processParentId)) {
        selected.push(serviceNode);
        // A selected service implicitly grants access to all of its features, so mark the
        // child feature nodes as selected too. `_syncTreeSelectionToForm` strips these when
        // computing the form payload, so this is purely a visual convenience.
        selected.push(...featureNodes);
      }
    }

    return { tree, selected };
  }

  /**
   * Enforces rules for the form logic.
   * For example, when "Access to all Services" is enabled, default values are set for certain form fields.
   */
  private _setupFormLogic(): void {
    // Apply rules for initial form state (without marking form dirty)
    this._applyCanViewAllServicesRules(this.form.controls.canViewAllServices.value, { markDirty: false });

    // React to future changes
    this.form.controls.canViewAllServices.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this._applyCanViewAllServicesRules(value);
    });
    // TODO(extra-services): drop the `extraServicesEnabled` guard once the feature ships and is always on.
    if (this.extraServicesEnabled) {
      this.form.controls.canModifyExtraServices.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this._syncCanViewExtraServicesState();
      });
    }
    this.form.controls.canAccessEmployeeProcesses.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // canViewAllServices already forces all nodes selected+disabled, so skip when it's on.
        if (!this.form.controls.canViewAllServices.value) {
          this._applyCanAccessEmployeeProcessesRules(value);
        }
      });
  }

  /**
   * When `canAccessEmployeeProcesses` is on, every Process Parent with `isVisibleToEmployeeUsers=true`
   * is implicitly accessible by the role, so we mark those nodes as selected and non-selectable
   * in the tree. The selections are stripped from the payload in `_syncTreeSelectionToForm`.
   */
  private _applyCanAccessEmployeeProcessesRules(
    canAccessEmployeeProcesses: boolean,
    { markDirty = true }: { markDirty?: boolean } = {},
  ): void {
    const employeeNodes = this._collectEmployeeVisibleNodes();
    if (canAccessEmployeeProcesses) {
      for (const node of employeeNodes) {
        if (!this.selectedServiceNodes.some((n) => n.key === node.key)) {
          this.selectedServiceNodes = [...this.selectedServiceNodes, node];
        }
        node.selectable = false;
      }
    } else {
      for (const node of employeeNodes) {
        node.selectable = node.data?.isValidSelection ?? true;
        if (!node.data) {
          continue;
        }
        // Keep the node if it was explicitly selected before the flag turned on, OR if its parent Service is selected.
        const parentServiceSelected = this.serviceTree.some(
          (service) =>
            service.children?.some((child) => child.key === node.key) &&
            this.selectedServiceNodes.some((n) => n.key === service.key),
        );
        if (!this._explicitlySelectedEmployeeIds.has(node.data.id) && !parentServiceSelected) {
          this.selectedServiceNodes = this.selectedServiceNodes.filter((n) => n.key !== node.key);
        }
      }
    }
    this._syncTreeSelectionToForm({ markDirty });
    this._updateTreeDisabledStates();
  }

  /**
   * Takes a snapshot of the employee-visible nodes currently present in `selectedServiceNodes`.
   * Meaningful only when `canAccessEmployeeProcesses` is off — otherwise every employee-visible
   * node is selected by the implicit-grant overlay and the snapshot would capture noise.
   */
  private _collectExplicitlySelectedEmployeeIds(): Set<number> {
    const employeeKeys = new Set(this._collectEmployeeVisibleNodes().map((n) => n.key));
    const ids = new Set<number>();
    for (const node of this.selectedServiceNodes) {
      if (employeeKeys.has(node.key) && node.data) {
        ids.add(node.data.id);
      }
    }
    return ids;
  }

  private _collectEmployeeVisibleNodes(): TreeNode<ServiceFeatureData>[] {
    const matches: TreeNode<ServiceFeatureData>[] = [];
    for (const serviceNode of this.serviceTree) {
      if (serviceNode.data?.isVisibleToEmployeeUsers && serviceNode.data.isValidSelection) {
        matches.push(serviceNode);
      }
      for (const featureNode of serviceNode.children ?? []) {
        if (featureNode.data?.isVisibleToEmployeeUsers && featureNode.data.isValidSelection) {
          matches.push(featureNode);
        }
      }
    }
    return matches;
  }

  // When "Access to all Services" is enabled:
  // - canShareProcesses must be false (implied by canViewAllServices)
  // - canSeeResponsiblePersons must be false (implied by canViewAllServices)
  // - accessToProcessParents must be empty (specific access is incompatible with full access)
  // - canAccessEmployeeProcesses is auto-enabled
  // - canViewAllPrivateTransactions becomes editable, but is not auto-enabled
  private _applyCanViewAllServicesRules(
    canViewAllServices: boolean,
    { markDirty = true }: { markDirty?: boolean } = {},
  ): void {
    if (canViewAllServices) {
      // Set to true visually to convey "included in canViewAllServices".
      // The actual payload overrides these back to false as the backend requires.
      this.form.controls.canShareProcesses.setValue(true);
      this.form.controls.canShareProcesses.disable();

      this.form.controls.canSeeResponsiblePersons.setValue(true);
      this.form.controls.canSeeResponsiblePersons.disable();

      this.form.controls.canAccessEmployeeProcesses.setValue(true);
      this.form.controls.canAccessEmployeeProcesses.disable();

      this.form.controls.canViewAllPrivateTransactions.enable();

      this._selectAllServiceNodes();
      this._syncTreeSelectionToForm({ markDirty });
      this._disableServiceTree(true);
    } else {
      this.form.controls.canShareProcesses.enable();
      this.form.controls.canSeeResponsiblePersons.enable();
      this.form.controls.canAccessEmployeeProcesses.enable();

      this.form.controls.canViewAllPrivateTransactions.setValue(false);
      this.form.controls.canViewAllPrivateTransactions.disable();

      this._disableServiceTree(false);
      // Re-apply employee-visible node disabling if `canAccessEmployeeProcesses` is still on.
      if (this.form.controls.canAccessEmployeeProcesses.value) {
        this._applyCanAccessEmployeeProcessesRules(true, { markDirty });
      }
    }

    // Full service access implies viewing extra services, so re-sync that lock whenever this toggles.
    this._syncCanViewExtraServicesState();
  }

  // Viewing extra services is implied by `canModifyExtraServices` and by `canViewAllServices`
  private _syncCanViewExtraServicesState(): void {
    if (!this.extraServicesEnabled) {
      return;
    }
    const impliedOn = this.form.controls.canViewAllServices.value || this.form.controls.canModifyExtraServices.value;
    if (impliedOn) {
      this.form.controls.canViewExtraServices.setValue(true);
      this.form.controls.canViewExtraServices.disable();
    } else {
      this.form.controls.canViewExtraServices.enable();
    }
  }

  private _selectAllServiceNodes(): void {
    const allNodes: TreeNode<ServiceFeatureData>[] = [];
    for (const serviceNode of this.serviceTree) {
      allNodes.push(serviceNode);
      if (serviceNode.children) {
        allNodes.push(...serviceNode.children);
      }
    }
    this.selectedServiceNodes = allNodes.filter((x) => x.data?.isValidSelection);
  }

  private _disableServiceTree(disabled: boolean): void {
    const disableNodesRecursively = (nodes: TreeNode<ServiceFeatureData>[]) => {
      for (const node of nodes) {
        // Don't modify nodes that aren't valid selections in the first place.
        if (node.data?.isValidSelection) {
          node.selectable = !disabled;
        }
        if (node.children) {
          disableNodesRecursively(node.children);
        }
      }
    };
    disableNodesRecursively(this.serviceTree);
  }

  /**
   * t(app.organization-settings.editAccessRoleDialog.createSuccess)
   * t(app.organization-settings.editAccessRoleDialog.updateSuccess)
   */
  public submitEditAccessRoleForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;

    this._save$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translocoService.translate(
              this.isCreateMode
                ? "app.organization-settings.editAccessRoleDialog.createSuccess"
                : "app.organization-settings.editAccessRoleDialog.updateSuccess",
            ),
          );
          this.ref.close(this.isCreateMode ? { created: true } : { updated: true });
        },
        error: () => {
          this.loading = false;
          this.toastService.error(this.translocoService.translate("app.common.errors.saveError"));
        },
      });
  }

  /** Upserts the role's permissions (custom roles only) and/or sets its RAN Expense access. */
  private _save$(): Observable<unknown> {
    const orgId = this.organization.id;

    // For built-in roles, the permissions form is read-only, so only the RAN Expense access level can be changed.
    if (this.accessRole?.isBuiltInRole) {
      return this._saveRanExpenseAccess$(this.accessRole.id);
    }

    const payload = this._buildUpsertPayload();

    if (this.isCreateMode) {
      return this.accessRoleService
        .createAccessRole$(orgId, payload)
        .pipe(switchMap((role) => this._saveRanExpenseAccess$(role.id)));
    }

    const accessRoleId = this.accessRole!.id;
    // If only the RAN Expense level changed, skip the role update (and its spurious "modified" audit entry).
    if (!this._permissionFieldsChanged()) {
      return this._saveRanExpenseAccess$(accessRoleId);
    }
    return this.accessRoleService
      .updateAccessRole$(orgId, accessRoleId, payload)
      .pipe(switchMap(() => this._saveRanExpenseAccess$(accessRoleId)));
  }

  // Calls the dedicated RAN Expense access endpoint, but only when the section is shown and the level changed.
  private _saveRanExpenseAccess$(accessRoleId: number): Observable<void> {
    if (!this.showRanExpensePermissions || !this.form.controls.ranExpenseAccess.dirty) {
      return of(undefined);
    }
    return this.accessRoleService.setRanExpenseAccess$(
      this.organization.id,
      accessRoleId,
      this.form.controls.ranExpenseAccess.value,
    );
  }

  private _buildUpsertPayload(): ApiUpsertAccessRoleRequest {
    const formValue = this.form.getRawValue();
    // When canViewAllServices is true, the UI shows canShareProcesses/canSeeResponsiblePersons
    // as checked (to convey "included"), but the backend requires them to be false.
    const viewAllOverride = formValue.canViewAllServices;
    const payload: ApiUpsertAccessRoleRequest = {
      name: formValue.name ?? "",
      color: formValue.color,
      canAcceptTos: formValue.canAcceptTos,
      canAccessEmployeeProcesses: formValue.canAccessEmployeeProcesses,
      canApproveProcesses: formValue.canApproveProcesses,
      canModifyAccessRoles: formValue.canModifyAccessRoles,
      canModifyApprovalGroups: formValue.canModifyApprovalGroups,
      canModifyInvitations: formValue.canModifyInvitations,
      canModifyUsers: formValue.canModifyUsers,
      canRemoveUsers: formValue.canRemoveUsers,
      canModifyAutomationUsers: formValue.canModifyAutomationUsers,
      canSeeResponsiblePersons: viewAllOverride ? false : formValue.canSeeResponsiblePersons,
      canShareProcesses: viewAllOverride ? false : formValue.canShareProcesses,
      canViewAllServices: formValue.canViewAllServices,
      canViewAllPrivateTransactions: viewAllOverride && formValue.canViewAllPrivateTransactions,
      canViewAuditLog: formValue.canViewAuditLog,
      accessToProcessParents: viewAllOverride ? [] : formValue.accessToProcessParents,
    };

    // TODO(extra-services): inline these into the payload above (always sent) once the feature is released
    if (this.extraServicesEnabled) {
      payload.canModifyExtraServices = formValue.canModifyExtraServices;
      payload.canViewExtraServices =
        formValue.canViewAllServices || formValue.canModifyExtraServices || formValue.canViewExtraServices;
    }

    return payload;
  }

  // Any dirty control other than the RAN Expense radio, which has its own dedicated save path.
  private _permissionFieldsChanged(): boolean {
    return Object.entries(this.form.controls).some(([key, control]) => key !== "ranExpenseAccess" && control.dirty);
  }

  public hasChanges(): boolean {
    return this.form.dirty;
  }

  // Built-in roles can still be saved to change their RAN Expense access when the section is shown.
  public get showSaveButton(): boolean {
    return !this.accessRole?.isBuiltInRole || this.showRanExpensePermissions;
  }

  public get canSave(): boolean {
    if (this.accessRole?.isBuiltInRole) {
      // The permissions form is read-only, so only a RAN Expense level change is saveable.
      return this.showRanExpensePermissions && this.form.controls.ranExpenseAccess.dirty;
    }
    return !this.form.invalid && (this.isCreateMode || this.hasChanges());
  }

  public openConfirmationDeleteDialog(): void {
    this.confirmationService.confirm({
      header: this.translocoService.translate("app.organization-settings.editAccessRoleDialog.deleteHeader"),
      message: this.translocoService.translate("app.organization-settings.editAccessRoleDialog.deleteConfirmation"),
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: this.translocoService.translate("app.common.cancel"),
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translocoService.translate("app.common.delete"),
        severity: "danger",
      },
      accept: () => {
        this.loading = true;
        this.accessRoleService
          .deleteAccessRole$(this.organization.id, this.accessRole!.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toastService.success(
                this.translocoService.translate("app.organization-settings.editAccessRoleDialog.deleteSuccess"),
              );
              this.ref.close({ deleted: true });
            },
            error: () => {
              this.loading = false;
              this.toastService.error(this.translocoService.translate("app.common.errors.deleteError"));
            },
          });
      },
    });
  }

  public onServiceTreeSelectionChange(): void {
    // Re-add any employee-visible nodes when parent service is de-selected
    if (this.form.controls.canAccessEmployeeProcesses.value) {
      for (const node of this._collectEmployeeVisibleNodes()) {
        if (!this.selectedServiceNodes.some((n) => n.key === node.key)) {
          this.selectedServiceNodes = [...this.selectedServiceNodes, node];
        }
      }
    }

    this._syncTreeSelectionToForm();
    this._updateTreeDisabledStates();

    if (!this.form.controls.canAccessEmployeeProcesses.value) {
      this._explicitlySelectedEmployeeIds = this._collectExplicitlySelectedEmployeeIds();
    }
  }

  /**
   * Derives the selected process parent IDs from the tree selection
   * and syncs them into the accessToProcessParents form control.
   */
  private _syncTreeSelectionToForm({ markDirty = true }: { markDirty?: boolean } = {}): void {
    const canAccessEmployeeProcesses = this.form.controls.canAccessEmployeeProcesses.value;
    const selectedIds: number[] = [];

    for (const node of this.selectedServiceNodes) {
      // When `canAccessEmployeeProcesses` is on, employee-visible nodes are implicitly granted
      // by that flag, so don't double-persist them in `accessToProcessParents`.
      if (canAccessEmployeeProcesses && node.data?.isVisibleToEmployeeUsers) {
        continue;
      }
      if (node.data?.type === "service") {
        selectedIds.push(node.data.id);
      } else if (node.data?.type === "feature") {
        // Only include feature if its parent service is not selected
        const parentSelected = this.selectedServiceNodes.some(
          (n) => n.data?.type === "service" && n.children?.some((child) => child.key === node.key),
        );
        if (!parentSelected) {
          selectedIds.push(node.data.id);
        }
      }
    }

    this.form.controls.accessToProcessParents.setValue(selectedIds);
    if (markDirty) {
      this.form.controls.accessToProcessParents.markAsDirty();
    }
  }

  private _updateTreeDisabledStates(): void {
    const canAccessEmployeeProcesses = this.form.controls.canAccessEmployeeProcesses.value;
    // Disable features when their parent service is selected
    for (const serviceNode of this.serviceTree) {
      const isServiceSelected = this.selectedServiceNodes.some((n) => n.key === serviceNode.key);

      if (serviceNode.children) {
        for (const featureNode of serviceNode.children) {
          // Keep employee-visible features non-selectable while `canAccessEmployeeProcesses`
          // implicitly grants them — otherwise this method would re-enable them on every
          // sibling selection change.
          if (canAccessEmployeeProcesses && featureNode.data?.isVisibleToEmployeeUsers) {
            featureNode.selectable = false;
          } else {
            featureNode.selectable = !isServiceSelected;
          }
        }
      }
    }
  }
}
